import { TrackState } from 'app/services/rtc/DeviceManager';
import { getStreamTrackState } from 'app/services/rtc/TrackState';
import { getStreamSettings } from 'app/models/rtc/selectors';

export interface IStreamFactory {
  (): Promise<MediaStream | undefined>;
}

interface IMediaStreamContainerUpdate {
  readonly stream?: MediaStream;
  readonly trackState?: TrackState;
  readonly currentSettings?: MediaTrackSettings[];
}

interface IMediaStreamContainerProperties extends IMediaStreamContainerUpdate {
  readonly streamFactory: IStreamFactory;
}

class MediaStreamContainer {
  public readonly stream?: MediaStream;
  public readonly streamFactory: IStreamFactory;
  public readonly trackState: TrackState;
  public readonly currentSettings: MediaTrackSettings[];

  public static create(streamFactory: IStreamFactory) {
    return new MediaStreamContainer({
      streamFactory,
    });
  }

  private constructor(
    properties: IMediaStreamContainerProperties,
  ) {
    const {
      streamFactory,
      stream,
      trackState = TrackState.Off,
      currentSettings = [],
    } = properties;

    this.streamFactory = streamFactory;
    this.stream = stream;
    this.trackState = trackState;
    this.currentSettings = currentSettings;
  }

  public async start() {
    if (this.trackState === TrackState.Active) {
      return this;
    }

    const stream = await this.streamFactory();

    return this.update({ stream })
               .updateTrackState()
               .dumpSettings();
  }

  public setEnabled(enabled: boolean) {
    if (!this.stream) { return this; }

    for (const track of this.stream.getTracks()) {
      track.enabled = enabled;
    }

    return this.update({
      trackState: getStreamTrackState(this.stream),
    });
  }

  public stopTracks() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }

    return this.update({ stream: undefined }).updateTrackState();
  }

  public updateTrackState() {
    const trackState = getStreamTrackState(this.stream);

    return this.update({
      trackState,
    });
  }

  public dumpSettings() {
    return this.update({
      currentSettings: getStreamSettings(this.stream),
    });
  }

  private update(
    update: IMediaStreamContainerUpdate,
  ) {
    return new MediaStreamContainer({
      ...this.properties,
      ...update,
    });
  }

  private get properties() {
    return {
      streamFactory: this.streamFactory,
      stream: this.stream,
      trackState: this.trackState,
      currentSettings: this.currentSettings,
    };
  }
}

export default MediaStreamContainer;
